<template>
  <q-page class="asf-page">
    <div class="asf-container">
      <!-- Header -->
      <div class="asf-header">
        <div class="asf-breadcrumb">
          <router-link to="/admin" class="asf-crumb">Admin</router-link>
          <span class="asf-crumb-sep">/</span>
          <router-link to="/admin/users" class="asf-crumb">Usuarios</router-link>
          <span class="asf-crumb-sep">/</span>
          <span class="asf-crumb asf-crumb--active">Ficha de alumno</span>
        </div>
        <h1 class="asf-title">
          {{ student ? `${student.firstName} ${student.lastName}` : 'Ficha de alumno' }}
        </h1>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="asf-center-msg">Cargando...</div>

      <!-- Error -->
      <div v-else-if="error" class="asf-center-msg">
        <p style="color: var(--oa-danger); margin-bottom: 8px;">{{ error }}</p>
        <q-btn flat no-caps label="Reintentar" style="color: var(--oa-gold-full);" @click="fetchFicha" />
      </div>

      <!-- Content -->
      <template v-else-if="student">
        <!-- Account data -->
        <div class="asf-card">
          <h2 class="asf-card-title">Datos de la cuenta</h2>
          <div class="asf-account-grid">
            <div>
              <p class="asf-label">Email</p>
              <p class="asf-value">{{ student.email }}</p>
            </div>
            <div>
              <p class="asf-label">Estado</p>
              <span class="asf-chip" :class="student.isActive ? 'asf-chip--active' : 'asf-chip--inactive'">
                {{ student.isActive ? 'Activo' : 'Inactivo' }}
              </span>
            </div>
          </div>
        </div>

        <!-- Enrolled courses -->
        <div class="asf-card">
          <h2 class="asf-card-title">Cursos inscriptos</h2>

          <div v-if="activeEnrollments.length === 0" class="asf-center-msg">
            Este alumno no está inscripto en ningún curso
          </div>

          <div v-else class="asf-enrollment-list">
            <div v-for="enrollment in activeEnrollments" :key="enrollment.id" class="asf-enrollment-row">
              <div class="asf-enrollment-info">
                <p class="asf-enrollment-title">{{ enrollment.course.title }}</p>
                <q-linear-progress
                  :value="enrollment.progressPct / 100"
                  size="8px"
                  rounded
                  track-color="grey-3"
                  color="accent"
                  class="asf-progress-bar"
                />
                <p class="asf-progress-label">{{ enrollment.progressPct }}% completado</p>

                <!-- Quiz results per lesson (EVAL-04) -->
                <div v-if="enrollment.quizResults.length" class="asf-quiz-list">
                  <p
                    v-for="qr in enrollment.quizResults"
                    :key="qr.lessonId"
                    class="asf-quiz-row"
                  >
                    {{ qr.lessonTitle }} — Quiz: {{ qr.correct }}/{{ qr.total }}
                  </p>
                </div>
                <p v-else class="asf-quiz-placeholder">Sin autoevaluaciones aún</p>
              </div>
              <q-btn
                flat
                no-caps
                label="Desinscribir"
                style="color: var(--oa-danger);"
                @click="confirmUnenroll(enrollment.id, enrollment.course.title)"
              />
            </div>
          </div>
        </div>

        <!-- Enroll picker -->
        <div class="asf-card">
          <h2 class="asf-card-title">Inscribir en curso</h2>
          <div class="asf-enroll-picker">
            <q-select
              v-model="selectedCourseId"
              :options="courseOptions"
              emit-value
              map-options
              dense
              outlined
              label="Curso publicado"
              class="asf-enroll-select"
            />
            <q-btn
              no-caps
              unelevated
              label="Inscribir"
              :loading="enrolling"
              :disable="!selectedCourseId"
              style="background: var(--oa-gold-full); color: var(--color-white-cap); font-weight: 600; border-radius: 8px;"
              @click="confirmEnroll"
            />
          </div>
        </div>
      </template>
    </div>
  </q-page>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { api } from 'boot/axios';
import { useQuasar } from 'quasar';

const route = useRoute();
const $q = useQuasar();

// ---------- Types ----------
interface EnrollmentCourse {
  id: string;
  title: string;
  slug: string;
}

interface QuizResultRow {
  lessonId: string;
  lessonTitle: string;
  correct: number;
  total: number;
}

interface EnrollmentRow {
  id: string;
  status: string;
  progressPct: number;
  enrolledAt: string;
  course: EnrollmentCourse;
  quizResults: QuizResultRow[];
}

interface StudentFicha {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  isActive: boolean;
  createdAt: string;
  enrollments: EnrollmentRow[];
}

interface CourseOption {
  label: string;
  value: string;
}

// ---------- State ----------
const loading = ref(true);
const error = ref<string | null>(null);
const student = ref<StudentFicha | null>(null);
const courseOptions = ref<CourseOption[]>([]);
const selectedCourseId = ref<string | null>(null);
const enrolling = ref(false);

// ---------- Computed ----------
const activeEnrollments = computed(() => {
  if (!student.value) return [];
  const active = student.value.enrollments.filter((e) => e.status === 'active');
  // Dedupe to the most recent active enrollment per course.id (response is enrolledAt desc)
  const seen = new Set<string>();
  const deduped: EnrollmentRow[] = [];
  for (const enrollment of active) {
    if (seen.has(enrollment.course.id)) continue;
    seen.add(enrollment.course.id);
    deduped.push(enrollment);
  }
  return deduped;
});

// ---------- Data Fetching ----------
async function fetchFicha() {
  loading.value = true;
  error.value = null;
  try {
    const { data } = await api.get(`/admin/users/${route.params.userId}`);
    student.value = data;
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : 'Error al cargar la ficha';
    error.value = msg;
  } finally {
    loading.value = false;
  }
}

async function fetchPublishedCourses() {
  try {
    const { data } = await api.get('/courses');
    courseOptions.value = data.map((c: { id: string; title: string }) => ({
      label: c.title,
      value: c.id,
    }));
  } catch {
    // Non-blocking: picker just stays empty
  }
}

// ---------- Actions ----------
async function confirmEnroll() {
  if (!student.value || !selectedCourseId.value) return;
  enrolling.value = true;
  try {
    await api.post(`/admin/users/${student.value.id}/enroll`, { courseId: selectedCourseId.value });
    $q.notify({ type: 'positive', message: 'Alumno inscripto' });
    selectedCourseId.value = null;
    await fetchFicha();
  } catch (err: unknown) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
    if (status === 409) {
      $q.notify({ type: 'negative', message: 'El alumno ya esta inscripto en este curso' });
    } else {
      $q.notify({ type: 'negative', message: msg || 'Error al inscribir al alumno' });
    }
  } finally {
    enrolling.value = false;
  }
}

function confirmUnenroll(enrollmentId: string, courseTitle: string) {
  if (!student.value) return;
  $q.dialog({
    title: 'Desinscribir alumno',
    message: `¿Seguro que querés desinscribir a ${student.value.firstName} de "${courseTitle}"?`,
    cancel: { label: 'Cancelar', flat: true },
    ok: { label: 'Desinscribir', flat: true, color: 'negative' },
    persistent: true,
  }).onOk(async () => {
    try {
      await api.patch(`/admin/users/${student.value!.id}/enrollments/${enrollmentId}`, { status: 'cancelled' });
      $q.notify({ type: 'positive', message: 'Alumno desinscripto' });
      await fetchFicha();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      $q.notify({ type: 'negative', message: msg || 'Error al desinscribir al alumno' });
    }
  });
}

// ---------- Lifecycle ----------
onMounted(() => {
  fetchFicha();
  fetchPublishedCourses();
});
</script>

<style scoped lang="scss">
.asf-page {
  padding: 24px;
  background: var(--color-seafoam, #e8f4f8);
}

.asf-container {
  max-width: 720px;
  margin: 0 auto;
}

.asf-header {
  margin-bottom: 24px;
}

.asf-breadcrumb {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: var(--oa-text-muted);
  margin-bottom: 8px;
}

.asf-crumb {
  color: var(--oa-text-muted);
  text-decoration: none;
  transition: color 0.15s;
}

.asf-crumb:hover {
  color: var(--oa-text-primary);
}

.asf-crumb--active {
  color: var(--oa-text-primary);
}

.asf-crumb-sep {
  color: var(--oa-text-disabled);
}

.asf-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0;
}

.asf-center-msg {
  padding: 32px;
  text-align: center;
  color: var(--oa-text-muted);
  font-size: 0.875rem;
}

.asf-card {
  background: var(--color-white-cap);
  border: 1px solid var(--oa-border);
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 16px;
}

.asf-card-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0 0 16px;
}

.asf-account-grid {
  display: flex;
  gap: 32px;
  flex-wrap: wrap;
}

.asf-label {
  font-size: 12px;
  color: var(--oa-text-muted);
  margin: 0 0 4px;
}

.asf-value {
  font-size: 14px;
  color: var(--oa-text-primary);
  margin: 0;
}

.asf-chip {
  display: inline-block;
  font-size: 12px;
  padding: 3px 10px;
  border-radius: 12px;
  font-weight: 500;
}

.asf-chip--active {
  background: color-mix(in srgb, var(--oa-success) 15%, transparent);
  color: var(--oa-success);
}

.asf-chip--inactive {
  background: color-mix(in srgb, var(--oa-danger) 15%, transparent);
  color: var(--oa-danger);
}

.asf-enrollment-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.asf-enrollment-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid color-mix(in srgb, var(--oa-border) 50%, transparent);
}

.asf-enrollment-row:last-child {
  border-bottom: none;
}

.asf-enrollment-info {
  flex: 1;
  min-width: 0;
}

.asf-enrollment-title {
  font-size: 14px;
  font-weight: 500;
  color: var(--oa-text-primary);
  margin: 0 0 6px;
}

.asf-progress-bar {
  margin-bottom: 4px;
}

.asf-progress-label {
  font-size: 12px;
  color: var(--oa-text-muted);
  margin: 0;
}

.asf-quiz-placeholder {
  font-size: 12px;
  color: var(--oa-text-disabled);
  margin: 4px 0 0;
  font-style: italic;
}

.asf-quiz-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 6px;
}

.asf-quiz-row {
  font-size: 12px;
  color: var(--oa-text-primary);
  margin: 0;
  padding: 3px 8px;
  border-left: 3px solid var(--color-ocean-light, #2e7fb5);
  background: color-mix(in srgb, var(--color-ocean-light, #2e7fb5) 8%, transparent);
  border-radius: 0 4px 4px 0;
}

.asf-enroll-picker {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.asf-enroll-select {
  flex: 1;
}
</style>
